import { useEffect, useState, type RefObject } from "react";

type Options = {
  readonly itemCount: number;
  readonly itemHeight: number;
  /** Extra rows kept above and below the viewport to hide fast scrolling. */
  readonly overscan?: number;
};

export type VirtualRange = {
  readonly start: number;
  readonly end: number;
};

/**
 * Windows a fixed-row-height list: returns the slice currently worth
 * rendering, so a 500-row leaderboard only puts ~15 rows in the DOM.
 */
export function useVirtualRows(scrollRef: RefObject<HTMLElement | null>, { itemCount, itemHeight, overscan = 5 }: Options): VirtualRange {
  const [range, setRange] = useState<VirtualRange>({ start: 0, end: 0 });

  useEffect(() => {
    const element = scrollRef.current;

    if (!element || itemHeight <= 0) return;

    const update = () => {
      const first = Math.floor(element.scrollTop / itemHeight);
      const visibleCount = Math.ceil(element.clientHeight / itemHeight);

      const start = Math.max(0, first - overscan);
      const end = Math.min(itemCount, first + visibleCount + overscan);

      setRange((current) => (current.start === start && current.end === end ? current : { start, end }));
    };

    update();

    element.addEventListener("scroll", update, { passive: true });

    const observer = new ResizeObserver(update);
    observer.observe(element);

    return () => {
      element.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [scrollRef, itemCount, itemHeight, overscan]);

  return range;
}
